import React, { useCallback, useState } from 'react'
import * as Device from 'expo-device'
import { Alert, StyleSheet } from 'react-native'
import { DS } from '../../design-system'
import { observer } from 'mobx-react-lite'
import { BottomSheetScrollView, BottomSheetTextInput } from '@gorhom/bottom-sheet'
import { events } from '../../models/analytics/analytics-events'
import { useRouter } from 'expo-router'
import { RootStoreSnapshot, useStore } from '@/models'
import { getSnapshot } from 'mobx-state-tree'
import Toast from 'react-native-toast-message'
import { GameStoreSnapshot } from '../../models/game/game.store'
import { SessionStoreSnapshot } from '../../models/session'

export type SupportFormProps = {
  placeHolder?: string
  shouldRoute?: boolean
  onClose?: () => void
}

export const SupportForm = observer(({ placeHolder, shouldRoute = true, onClose }: SupportFormProps) => {
  const router = useRouter()
  const store = useStore()

  const [formState, setFormState] = useState({
    subject: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')

  const handleChange = (field: string) => (value: string) => {
    setFormState((prev) => ({ ...prev, [field]: value }))
    if (error) setError('')
  }

  const showToast = (text1: string, text2: string, type: string) => {
    Toast.show({
      type: type,
      text1: text1,
      text2: text2
    })
  }

  const pruneSnapshot = (snap: RootStoreSnapshot) => {
    const prunedSnap = {
      session: {} as SessionStoreSnapshot,
      game: {} as GameStoreSnapshot,
      snapshot: {} as GameStoreSnapshot['game'],
      env: snap.environment,
      network: snap.network
    }

    try {
      // `session` store snapshot is readonly, can't prune or modify so rebuild
      const session = JSON.parse(JSON.stringify(snap.session))
      // Token config is now in Zustand, not in MST session
      prunedSnap.session = session || {}
    } catch (err) {
      console.error('Could not stringify then parse `session` JSON!', err)
      // Use the read-only version without pruning out anything.
      prunedSnap.session = snap.session
    }

    try {
      const game = JSON.parse(JSON.stringify(snap.game))
      prunedSnap.snapshot = game.game || {}

      if (game) {
        game.game = '<see_snapshot>'
        game.leaderboardData = '<removed>'
        game.liveCommunityGames = '<removed>'
        game.scheduledCommunityGames = '<removed>'
        game.teachingTableScheduleGames = '<removed>'
        game.targetPositions = '<removed>'
      }
      prunedSnap.game = game || {}
    } catch (err) {
      console.error('Could not stringify then parse `game` JSON!', err)
      // Use the read-only version without pruning out anything.
      prunedSnap.game = snap.game
    }

    return prunedSnap
  }

  // WARNING can be risky to ignore. But list is quite long and function is too much.
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const handleSubmit = useCallback(async () => {
    // Basic validation
    if (!(formState.subject && formState.message)) {
      setError('Please fill in all required fields')
      return
    }

    try {
      setIsSubmitting(true)
      store.analytics.setEvent(events.support?.support_form_submitted || 'support_form_submitted')
      const snap = getSnapshot(store)
      const prunedSnap = pruneSnapshot(snap)

      const emailSubject = `App Support Request: ${formState.subject}`
      const emailBody = `
        Name: ${store.session.user?.name}
        <br/>
        Email: ${store.session.user?.email}
        <br/>
        Game: ${prunedSnap.snapshot?.gameId || '(not in game)'}
        <br/>
        <br/>

        Message:
        ${formState.message}
      `
      if (__DEV__ && !Device.isDevice) {
        console.log(
          `S: ${emailSubject} B: ${emailBody} D: ${__DEV__} R: ${shouldRoute}`,
          JSON.stringify(prunedSnap, null, 2)
        )
      } else {
        await store.api.mutateSendSupportEmail({
          inputEmail: {
            subject: emailSubject,
            body: emailBody,
            jsonString: JSON.stringify(prunedSnap)
          }
        })
      }

      showToast('Success', "Message sent. We'll get back to you soon.", 'success')
    } catch (err) {
      showToast('Error', 'Something went wrong. Please try again later.', 'error')
      console.error(err)
    } finally {
      setIsSubmitting(false)
      console.log('handleSubmit', { shouldRoute, onClose })
      if (shouldRoute) {
        if (router.canDismiss()) {
          router.dismiss()
        }
      } else if (onClose) {
        onClose()
      }
    }
  }, [formState, store.analytics.setEvent, store.session.user, shouldRoute])

  const handleClose = () => {
    console.log('handleClose', { shouldRoute, onClose })
    if (shouldRoute) {
      if (router.canDismiss()) {
        return router.dismiss()
      }
      router.back()
    } else if (onClose) {
      onClose()
    }
  }

  return (
    <BottomSheetScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.container}>
      <DS.Text variant="Heading 3/Medium" style={styles.title}>
        Contact Support
      </DS.Text>

      {submitted ? (
        <DS.Frame style={styles.successContainer}>
          <DS.Icon name="Check" style={styles.successIcon} />
          <DS.Text variant="Body Small/Regular" style={styles.successText}>
            Thank you! Your message has been sent. Our support team will get back to you soon.
          </DS.Text>
          <DS.Button variant="Large/Primary" type="On Light" onPress={handleClose} style={styles.button}>
            Close
          </DS.Button>
        </DS.Frame>
      ) : (
        <>
          <DS.Frame style={styles.formGroup}>
            <DS.Text variant="Body Medium/Regular" style={styles.label}>
              Subject
            </DS.Text>
            <BottomSheetTextInput
              autoFocus={true}
              value={formState.subject}
              onChangeText={handleChange('subject')}
              placeholder={placeHolder || 'What is this regarding?'}
              style={styles.input}
            />
          </DS.Frame>

          <DS.Frame style={styles.formGroup}>
            <DS.Text variant="Body Medium/Regular" style={styles.label}>
              Message *
            </DS.Text>
            <BottomSheetTextInput
              value={formState.message}
              onChangeText={handleChange('message')}
              placeholder="How can we help you?"
              multiline
              numberOfLines={5}
              style={styles.textArea}
            />
          </DS.Frame>
          <DS.Frame style={styles.errorFrame}>
            <DS.Text variant="Body Small/Regular" style={styles.errorText}>
              {error ? error : ''}
            </DS.Text>
          </DS.Frame>

          <DS.Button
            type="On Light"
            variant="Large/Primary"
            onPress={handleSubmit}
            style={styles.button}
            disabled={isSubmitting}
          >
            Submit
          </DS.Button>
        </>
      )}
    </BottomSheetScrollView>
  )
})

export default SupportForm

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    paddingBottom: 24,
    paddingHorizontal: 24,
    backgroundColor: DS.palette['Brand/Cream']
  },
  scrollView: {
    width: '100%'
  },
  title: {
    textAlign: 'center',
    paddingBottom: 6
  },
  formGroup: {
    marginBottom: 20,
    width: '100%'
  },
  errorFrame: {
    height: 16,
    marginBottom: 4
  },
  label: {
    marginBottom: 8,
    color: DS.palette['Global/Black']
  },
  input: {
    width: '100%',
    backgroundColor: 'white',
    borderRadius: 8,
    padding: 12
  },
  textArea: {
    width: '100%',
    height: 120,
    backgroundColor: 'white',
    borderRadius: 8,
    padding: 12,
    textAlignVertical: 'top'
  },
  button: {
    width: '100%'
  },
  errorText: {
    color: DS.palette['Utility/Warning'],
    fontSize: 14
  },
  successContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    flex: 1
  },
  successIcon: {
    fontSize: 48,
    color: DS.palette['Utility/Success'],
    marginBottom: 16
  },
  successText: {
    textAlign: 'center',
    marginBottom: 24,
    color: DS.palette['Global/Black']
  }
})
